import { Item as RadixItem } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  type ElementType,
  forwardRef,
  type ReactEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import {
  AriaDisabledProps,
  AriaLabelingProps,
} from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { parseBoolean } from '../../core/utils/parse-boolean.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useTooltipState } from '../../overlays/hooks/useTooltipState.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';
import { MobileMenuModeContext } from './contexts/MobileMenuModeContext.js';
import { useIsMenuItemHovered } from './hooks/useIsMenuItemHovered.js';
import { useMenuContext } from './hooks/useMenuContext.js';
import { getItemSlots } from './item-slots.js';
import { MenuItemTooltipWrapper } from './ItemTooltip.js';
import * as styles from './Menu.sty.js';
import { MobileMenuItem } from './MobileMenuItem.js';

/**
 * @public
 */
export interface MenuItemOwnProps
  extends
    WithChildren,
    AriaLabelingProps,
    AriaDisabledProps,
    StylingProps,
    DataTestId {
  /**
   * Whether the menu item is disabled.
   * If `true`, it prevents the user from interacting with the item and removes
   * it from the tab order and keyboard navigation.
   *
   * For a soft-disabled item that stays focusable, is announced to screen
   * readers, and can reveal its `Menu.ItemTooltip` on desktop, set
   * `aria-disabled` instead. A soft-disabled item stays interactive and still
   * fires `onSelect`, but selecting it does not close the menu.
   */
  disabled?: boolean;
  /**
   * Optional text used for typeahead purposes. By default, the typeahead
   * behavior will use the `.textContent` of the item. Use this when the content
   * is complex, or you have non-textual content inside.
   */
  textValue?: string;
  /*
   * TODO: The onSelect from the Radix MenuItem actually emits a native Event instead of a react synthetic event.
   * When trying to fix that, our the app toolkits version of typescript ~4.9 bailed due to the type complexity. Right now there is no way
   * to correct the typing on the event and keep a compatible type version.
   * This limitiation also tracks through to the `ActionGroup` and `AppHeader`.
   * APPDEV-8522
   */
  /**
   * Event handler called when the user selects an item (via mouse or keyboard).
   * Calling `event.preventDefault()` in this handler prevents the dropdown menu
   * from closing when selecting that item.
   */
  onSelect: ReactEventHandler;
}

/**
 * @public
 */
export type MenuItemProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  MenuItemOwnProps
>;

/**
 * The component that contains the dropdown menu Items.
 * @public
 */
export const Item: <E extends ElementType = 'div'>(
  props: MenuItemProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    {
      children,
      disabled = false,
      'aria-disabled': ariaDisabledProp,
      onSelect,
      textValue,
      as = 'div' as E,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: MenuItemProps<E>,
    forwardedRef: typeof remainingProps.ref,
  ) => {
    const isMobile = useContext(MobileMenuModeContext);
    const { setIsOpen } = useMenuContext();
    const slots = getItemSlots(children);

    // A hard `disabled` item is removed from focus and keyboard navigation and
    // cannot be selected. A soft-disabled item (`aria-disabled`) keeps the
    // disabled styling and is announced as disabled, but stays focusable and
    // interactive - it can show its tooltip on desktop and still fires
    // `onSelect`, while never closing the menu (nothing was committed).
    // Hard `disabled` wins if both are set.
    const isSoftDisabled = !disabled && parseBoolean(ariaDisabledProp) === true;
    const isDisabled = disabled || isSoftDisabled;
    const {
      isOpen,
      open: openTooltip,
      close: closeTooltip,
    } = useTooltipState({
      defaultOpen: false,
    });
    const [tooltipTriggerRef, setTooltipTriggerRef] =
      useState<HTMLElement | null>(null);
    const { focusProps, focusClassName } = useFocusRing({
      disabled,
    });
    const isTooltipHovered = useRef(false);
    const { isItemHovered, hoverProps } = useIsMenuItemHovered();

    useEffect(() => {
      if (!isOpen) {
        return;
      }
      window.addEventListener('scroll', closeTooltip, { capture: true });
      return () =>
        window.removeEventListener('scroll', closeTooltip, { capture: true });
    }, [isOpen, closeTooltip]);

    if (isMobile) {
      return (
        <>
          <MobileMenuItem
            ref={forwardedRef}
            data-testid={dataTestId}
            disabled={disabled}
            textValue={textValue}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-details={ariaDetails}
          >
            <Flex
              ref={setTooltipTriggerRef}
              role="menuitem"
              aria-disabled={isDisabled}
              className={clsx(
                textStyleCSS({ textStyle: 'base-emphasized' }),
                !isItemHovered && focusClassName,
                styles.item({ disabled: isDisabled }),
                consumerClassName,
              )}
              style={consumerStyle}
              disabled={disabled}
              as={as satisfies ElementType}
              {..._mergeProps(focusProps, hoverProps, {
                onClick: (event: MouseEvent) => {
                  if (disabled) {
                    event.stopPropagation();
                    event.preventDefault();
                    return;
                  }
                  onSelect(event);
                  // A soft-disabled item notifies the consumer but performs no
                  // committing action, so the panel stays open.
                  if (!isSoftDisabled && !event.defaultPrevented) {
                    setIsOpen(false);
                  }
                },
                onFocus: () => slots.tooltip && openTooltip(true),
                onBlur: () =>
                  slots.tooltip &&
                  requestAnimationFrame(() => {
                    if (!isTooltipHovered.current) {
                      closeTooltip();
                    }
                  }),
                onMouseEnter: () => slots.tooltip && openTooltip(true),
                onMouseLeave: () =>
                  slots.tooltip &&
                  requestAnimationFrame(() => {
                    if (!isTooltipHovered.current) {
                      closeTooltip();
                    }
                  }),
              })}
              {...remainingProps}
            >
              {slots.prefix}
              <div className={styles.itemChildren}>{slots.freeform}</div>
              {slots.suffix}
            </Flex>
          </MobileMenuItem>
          {isOpen && (
            <MenuItemTooltipWrapper
              triggerElement={tooltipTriggerRef}
              onMouseEnter={() => {
                isTooltipHovered.current = true;
              }}
              onMouseLeave={() => {
                isTooltipHovered.current = false;
                closeTooltip();
              }}
            >
              {slots.tooltip}
            </MenuItemTooltipWrapper>
          )}
        </>
      );
    }

    return (
      <>
        <RadixItem
          data-testid={dataTestId}
          disabled={disabled}
          textValue={textValue}
          ref={forwardedRef}
          // Radix only receives the hard `disabled` (so a soft-disabled item
          // keeps its place in roving focus), which means Radix treats a
          // soft-disabled item as selectable and closes the menu after a
          // select. Cancelling its select event keeps the menu open: the
          // consumer's `onSelect` still fires from `onClick` below, but no
          // committing action took place, so the menu must not close.
          onSelect={
            isSoftDisabled ? (event) => event.preventDefault() : undefined
          }
          {..._mergeProps(focusProps, hoverProps, {
            onClick: (event: MouseEvent) => {
              if (disabled) {
                event.stopPropagation();
                event.preventDefault();
                return;
              }

              const nativeEvent = event.nativeEvent;
              if (
                nativeEvent.button !== 0 ||
                nativeEvent.metaKey ||
                nativeEvent.ctrlKey ||
                nativeEvent.shiftKey
              ) {
                return; // let the browser handle the event
              }

              onSelect(event);
            },
            onFocus: () => slots.tooltip && openTooltip(true),
            onBlur: () =>
              slots.tooltip &&
              // Ensures isTooltipHovered state is set correctly when the tooltip is hovered.
              // Otherwise, the tooltip would close before the onMouseLeave event of the tooltip triggers
              // and the isTooltipHovered state would never be set.
              requestAnimationFrame(() => {
                if (!isTooltipHovered.current) {
                  closeTooltip();
                }
              }),
            className: consumerClassName,
            style: consumerStyle,
          })}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-details={ariaDetails}
          asChild
        >
          <Flex
            {...remainingProps}
            className={clsx(
              textStyleCSS({ textStyle: 'base-emphasized' }),
              !isItemHovered && focusClassName,
              styles.item({ disabled: isDisabled }),
            )}
            disabled={disabled}
            // TODO LeMa: role actually belongs on top-level element (check for side effects regarding tests after moving the attribute!)
            role="menuitem"
            aria-disabled={isDisabled}
            ref={setTooltipTriggerRef}
            as={as satisfies ElementType}
          >
            {slots.prefix}
            <div className={styles.itemChildren}>{slots.freeform}</div>
            {slots.suffix}
          </Flex>
        </RadixItem>
        {isOpen &&
          createPortal(
            <MenuItemTooltipWrapper
              triggerElement={tooltipTriggerRef}
              onMouseEnter={() => {
                isTooltipHovered.current = true;
              }}
              onMouseLeave={() => {
                isTooltipHovered.current = false;
                closeTooltip();
              }}
            >
              {slots.tooltip}
            </MenuItemTooltipWrapper>,
            document.body,
          )}
      </>
    );
  },
);

(Item as typeof Item & { displayName: string }).displayName = 'Menu.Item';
